import React, {useCallback, useRef} from 'react';
import translate from '@/utils/translate';
import {CreatorBrandSafetyVettingStatus} from '@/types/creatorBrandSafetyVetting';
import useCreatorsVirtualizedGridProps from '@/views/creators/hooks/use-creators-virtualized-grid-props/useCreatorsVirtualizedGridProps';
import CreatorCard from '@/components/creator-card';
import {CreatorMenuItemProps} from '@/components/creator-card/CreatorOverflowMenuButton';
import VirtualizedGrid from '@/components/virtualized-grid';
import useAcceptCreator from '../../hooks/use-accept-creator/useAcceptCreator';
import useCreatorRowActions from '../../hooks/use-creator-row-actions';
import type {MenuItemConfig} from '../../hooks/use-creator-row-menu-items/useCreatorRowMenuItems';
import {getCreatorCardCtaConfig} from '../../types/creatorCardCtaConfig';
import {SAFE_COLLAB_STATUS} from '../../types/creatorTabTypes';
import type {
  CampaignCreatorCounts,
  CampaignCreatorRow,
  SafeCollabStatus,
} from '../../types/creatorTabTypes';
import hasPendingNegotiation from '../../utils/hasPendingNegotiation';
import styles from './CreatorsCardGrid.module.scss';

const EMPTY_CATEGORIES: string[] = [];
const AGGREGATE_PLATFORM = 'aggregate';

const NOOP = () => {};

const SAFE_COLLAB_TO_VETTING_STATUS: Record<
  SafeCollabStatus,
  CreatorBrandSafetyVettingStatus | undefined
> = {
  [SAFE_COLLAB_STATUS.Vetted]: 'approved',
  [SAFE_COLLAB_STATUS.Warning]: 'declined',
  [SAFE_COLLAB_STATUS.NotVetted]: 'ready_for_review',
};

function toOverflowMenuItems(items: MenuItemConfig[]): CreatorMenuItemProps[] {
  return items
    .filter((item) => item.visible)
    .map((item) => ({
      iconName: item.icon,
      title: item.label,
      onClick: item.onClick,
      disabled: item.disabled,
      tooltip: item.tooltip,
    }));
}

interface CampaignCreatorCardProps {
  row: CampaignCreatorRow;
  isSelectMode: boolean;
  isSelected: boolean;
  isLastViewed?: boolean;
  hasDraft?: boolean;
  onSelectChange?: (creatorId: string, isSelected: boolean) => void;
  onCardClick?: (row: CampaignCreatorRow) => void;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
  onMessageCreator?: (row: CampaignCreatorRow) => void;
  acceptCreator: (row: CampaignCreatorRow) => void;
  isAccepting: boolean;
}

function CampaignCreatorCard({
  row,
  isSelectMode,
  isSelected,
  isLastViewed = false,
  hasDraft = false,
  onSelectChange,
  onCardClick,
  onActionComplete,
  onMessageCreator,
  acceptCreator,
  isAccepting,
}: CampaignCreatorCardProps) {
  const {menuItems, actionModals} = useCreatorRowActions({
    creator: row,
    onActionComplete,
    onCloseMenu: NOOP,
  });

  const overflowMenuItemsCallBack = useCallback(
    async () => toOverflowMenuItems(menuItems),
    [menuItems]
  );

  const ctaConfig = getCreatorCardCtaConfig(row.applicantStatus);

  const creatorCardCtaProps = {
    buttonText: () => translate(ctaConfig.label),
    onClick: (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      switch (ctaConfig.id) {
        case 'accept':
          acceptCreator(row);
          break;
        case 'message':
          onMessageCreator?.(row);
          break;
        case 'view':
          onCardClick?.(row);
          break;
        default:
          break;
      }
    },
    isDisabled: () => {
      switch (ctaConfig.id) {
        case 'accept':
          return !row.paidGigId || hasPendingNegotiation(row) || isAccepting;
        case 'message':
          return !row.paidGigId;
        default:
          return false;
      }
    },
  };
  const vettingDecision = SAFE_COLLAB_TO_VETTING_STATUS[row.safeCollabStatus];

  const socialNetworks = [
    {
      provider: AGGREGATE_PLATFORM,
      url: '',
      followersCount: row.followers,
      engagementRate: row.engagementRate,
    },
  ];

  return (
    <div
      className={styles.cardWrapper}
      onClick={onCardClick ? () => onCardClick(row) : undefined}
      role={onCardClick ? 'button' : undefined}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={onCardClick ? 0 : undefined}
      onKeyDown={
        onCardClick
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === ' ') {
                e.preventDefault();
                onCardClick(row);
              } else if (e.key === 'Enter') {
                onCardClick(row);
              }
            }
          : undefined
      }
    >
      <CreatorCard
        id={row.id}
        fullName={row.creatorName}
        handle={row.handle}
        age={row.age?.toString()}
        location={row.location ?? undefined}
        email=""
        profileImage={row.avatarUrl ?? undefined}
        categories={EMPTY_CATEGORIES}
        images={[
          row.portfolioImages[0] ?? '',
          row.portfolioImages[1] ?? '',
          row.portfolioImages[2] ?? '',
        ]}
        socialNetworks={socialNetworks}
        selectedSocialNetworkProvider={AGGREGATE_PLATFORM}
        hideSocialIcons
        rating={row.campaignHistoryRating ?? undefined}
        vettingDecision={vettingDecision}
        isShortlisted={row.isShortlisted}
        isGigCompleted={row.requirementsMet || row.gigInTerminalState}
        creatorCardCtaProps={isSelectMode ? undefined : creatorCardCtaProps}
        isSelectMode={isSelectMode}
        isSelected={isSelected}
        isLastViewed={isLastViewed}
        hasDraft={hasDraft}
        onSelectChange={onSelectChange}
        isOverflowMenuEnabled={!isSelectMode}
        creatorCardOverflowMenuItemsCallBack={overflowMenuItemsCallBack}
      />
      <span
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {actionModals}
      </span>
    </div>
  );
}

interface CreatorsCardGridProps {
  data: CampaignCreatorRow[];
  onCardClick?: (row: CampaignCreatorRow) => void;
  onEndReached?: () => void;
  isSelectMode?: boolean;
  selectedCreatorIds?: Set<string>;
  lastViewedCreatorId?: string | null;
  hasMessageDraft?: (row: CampaignCreatorRow) => boolean;
  onSelectChange?: (creatorId: string, isSelected: boolean) => void;
  emptyStateComponent?: React.ReactNode;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
  onMessageCreator?: (row: CampaignCreatorRow) => void;
}

function CreatorsCardGrid({
  data,
  onCardClick,
  onEndReached,
  isSelectMode = false,
  selectedCreatorIds,
  lastViewedCreatorId,
  hasMessageDraft,
  onSelectChange,
  emptyStateComponent,
  onActionComplete,
  onMessageCreator,
}: CreatorsCardGridProps) {
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const gridProps = useCreatorsVirtualizedGridProps(gridContainerRef, false);
  const {acceptCreator, isAccepting} = useAcceptCreator(onActionComplete);

  const renderCreatorCard = useCallback(
    function renderCreatorCard(row: CampaignCreatorRow) {
      return (
        <CampaignCreatorCard
          row={row}
          isSelectMode={isSelectMode}
          isSelected={selectedCreatorIds?.has(row.id) ?? false}
          isLastViewed={lastViewedCreatorId === row.id}
          hasDraft={hasMessageDraft?.(row) ?? false}
          onSelectChange={onSelectChange}
          onCardClick={onCardClick}
          onActionComplete={onActionComplete}
          onMessageCreator={onMessageCreator}
          acceptCreator={acceptCreator}
          isAccepting={isAccepting}
        />
      );
    },
    [
      onCardClick,
      isSelectMode,
      selectedCreatorIds,
      lastViewedCreatorId,
      hasMessageDraft,
      onSelectChange,
      onActionComplete,
      onMessageCreator,
      acceptCreator,
      isAccepting,
    ]
  );

  if (data.length === 0 && emptyStateComponent) {
    return emptyStateComponent;
  }

  return (
    <div ref={gridContainerRef} className={styles.outerContainer}>
      <VirtualizedGrid
        {...gridProps}
        containerClassName={styles.gridContainer}
        data={data}
        renderItem={renderCreatorCard}
        onEndReached={onEndReached}
        tabIndex={0}
      />
    </div>
  );
}

export default CreatorsCardGrid;
